import React, { useState, useRef, useEffect } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  numInputs?: number;
  separator?: React.ReactNode;
  shouldAutoFocus?: boolean;
  className?: string;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  numInputs = 4,
  separator,
  shouldAutoFocus = false,
  className = '',
}) => {
  const [otp, setOtp] = useState<string[]>(new Array(numInputs).fill(''));
  const [activeOtpIndex, setActiveOtpIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (shouldAutoFocus) {
      inputRefs.current[0]?.focus();
    }
  }, [shouldAutoFocus]);

  useEffect(() => {
    const otpValue = value.split('');
    const newOtp = [...otp];
    for (let i = 0; i < numInputs; i++) {
      newOtp[i] = otpValue[i] || '';
    }
    setOtp(newOtp);
  }, [value, numInputs]);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '');

    if (numericValue.length > 1) {
      const newOtp = numericValue.slice(0, numInputs).split('');
      setOtp(newOtp);
      onChange(newOtp.join(''));
      const lastFilledIndex = newOtp.findIndex(digit => digit === '');
      const focusIndex = lastFilledIndex === -1 ? numInputs - 1 : lastFilledIndex;
      inputRefs.current[focusIndex]?.focus();
      setActiveOtpIndex(focusIndex);
      return;
    }

    const newOtp = [...otp];
    newOtp[activeOtpIndex] = numericValue;
    setOtp(newOtp);
    onChange(newOtp.join(''));

    if (numericValue && activeOtpIndex < numInputs - 1) {
      setActiveOtpIndex(activeOtpIndex + 1);
      inputRefs.current[activeOtpIndex + 1]?.focus();
    }
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (!otp[activeOtpIndex] && activeOtpIndex > 0) {
        setActiveOtpIndex(activeOtpIndex - 1);
        inputRefs.current[activeOtpIndex - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[activeOtpIndex] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
      }
    }
  };

  const handleOnPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '');
    const newOtp = pastedData.slice(0, numInputs).split('');
    setOtp(newOtp);
    onChange(newOtp.join(''));
    const lastFilledIndex = newOtp.findIndex(digit => digit === '');
    const focusIndex = lastFilledIndex === -1 ? numInputs - 1 : lastFilledIndex;
    inputRefs.current[focusIndex]?.focus();
    setActiveOtpIndex(focusIndex);
  };

  return (
    <div className={`flex items-center gap-2 ${className}`} dir="ltr">
      {otp.map((digit, index) => (
        <React.Fragment key={index}>
          <input
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            onPaste={handleOnPaste}
            onFocus={() => setActiveOtpIndex(index)}
            className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors"
            autoComplete="one-time-code"
          />
          {index < numInputs - 1 && separator}
        </React.Fragment>
      ))}
    </div>
  );
};

export default OtpInput;
